import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Play, Trash2, Edit2, ToggleLeft, ToggleRight } from 'lucide-react';
import { fetchAdminSurveys, fetchTestSurveys, deleteTestSurvey, toggleTestSurveyStatus, simulateTestSurvey } from '../../lib/adminApi';

type SurveyRow = {
  id: string;
  title: string;
  provider_name: string;
  category: string | null;
  status: string;
  points_value: number;
  estimated_length_minutes: number;
  start_count: number;
  completion_count: number;
  completion_rate: number;
  created_at: string;
  metadata?: { cpi?: number; ir?: number; completesTarget?: number };
};

export default function AdminSurveys() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | 'test'>('all');

  const { data: allData, isLoading: allLoading } = useQuery({ queryKey: ['admin', 'surveys'], queryFn: fetchAdminSurveys });
  const { data: testData, isLoading: testLoading } = useQuery({ queryKey: ['admin', 'surveys', 'test'], queryFn: fetchTestSurveys });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTestSurvey(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => toggleTestSurveyStatus(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] });
    },
  });

  const simulateMutation = useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: 'complete' | 'screenout' | 'quotafull' | 'security-terminate' }) => simulateTestSurvey(id, outcome),
    onSuccess: (data: { redirectUrl: string }) => {
      window.location.href = data.redirectUrl;
    },
  });

  const surveys: SurveyRow[] = tab === 'test' ? (testData?.surveys ?? []) : (allData?.surveys ?? []);
  const loading = tab === 'test' ? testLoading : allLoading;

  function statusBadge(status: string) {
    if (status === 'active') return 'bg-green-900/50 text-green-400';
    if (status === 'paused') return 'bg-amber-900/50 text-amber-400';
    if (status === 'draft') return 'bg-slate-700 text-slate-400';
    return 'bg-red-900/50 text-red-400';
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-display text-white">Surveys</h1>
        <Link
          to="/admin/surveys/new"
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Test Survey
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'all' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          All Surveys
        </button>
        <button
          onClick={() => setTab('test')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'test' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Test Surveys
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading...</div>
      ) : (
        <div className="bg-slate-800 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Completes</th>
                <th className="px-4 py-3 text-right">CPI</th>
                <th className="px-4 py-3 text-right">LOI</th>
                <th className="px-4 py-3 text-right">IR%</th>
                <th className="px-4 py-3 text-right">Points</th>
                <th className="px-4 py-3">Last Activity</th>
                {tab === 'test' && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {surveys.map((s) => (
                <tr key={s.id} className="hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-3 text-white max-w-[240px]">
                    <div className="truncate font-medium">{s.title}</div>
                    <div className="text-xs text-slate-500">{s.id.slice(0, 8)}... · {s.provider_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(s.status)}`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
                    {s.completion_count} / {s.metadata?.completesTarget && s.metadata.completesTarget < 999999 ? s.metadata.completesTarget : 'unlimited'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300 tabular-nums">${(s.metadata?.cpi ?? (s.points_value / 35)).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{s.estimated_length_minutes}m</td>
                  <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{s.metadata?.ir ?? '—'}%</td>
                  <td className="px-4 py-3 text-right text-white tabular-nums">{s.points_value}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                  {tab === 'test' && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleMutation.mutate(s.id)}
                          title={s.status === 'active' ? 'Pause' : 'Activate'}
                          className="p-1.5 rounded hover:bg-slate-600 text-slate-400 hover:text-white transition-colors"
                        >
                          {s.status === 'active' ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/surveys/${s.id}/edit`)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-slate-600 text-slate-400 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => simulateMutation.mutate({ id: s.id, outcome: 'complete' })}
                          title="Simulate Complete"
                          className="p-1.5 rounded hover:bg-slate-600 text-green-400 hover:text-green-300 transition-colors"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { if (confirm('Delete this test survey?')) deleteMutation.mutate(s.id); }}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-slate-600 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {surveys.length === 0 && <p className="text-center text-slate-500 py-8">No surveys found.</p>}
        </div>
      )}
    </div>
  );
}
